/**
 * MovimientosTab - Lista de movimientos de tesorería (estilo DocumentosList)
 * Muestra lista de movimientos con filtros y opciones de edición
 */
import React, { useState, useMemo } from 'react';
import {
  Paper, Box, Typography, CircularProgress, Chip, FormControl, InputLabel, Select, MenuItem,
  List, ListItemButton, ListItemText, Divider, TextField, Tooltip
} from '@mui/material';
import { formatearFecha, formatearMonto } from '../../utils/formatters';

const MovimientosTab = ({
  movimientos = [],
  loading = false,
  onMovimientoSeleccionado = () => {},
  movimientoSeleccionado = null,
  cuentas = [],
  rutEmpresa = '',
  anio = new Date().getFullYear(),
  mes = new Date().getMonth() + 1
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [mostrarSoloProcesados, setMostrarSoloProcesados] = useState(true);

  // Crear mapa de cuentas para lookup rápido
  const mapaCuentas = useMemo(() => {
    const mapa = {};
    cuentas.forEach(cuenta => {
      // Usar codigo como clave
      mapa[cuenta.codigo] = cuenta;
      // También indexar por ID en caso de que sea necesario
      if (cuenta.id) {
        mapa[cuenta.id] = cuenta;
      }
    });
    return mapa;
  }, [cuentas]);

  // Función helper para obtener info de la cuenta
  const getCuentaInfo = (codigoOId) => {
    if (!codigoOId) return null;
    return mapaCuentas[codigoOId];
  };

  // Función helper para mostrar nombre de cuenta
  const getNombreCuenta = (codigoOId) => {
    const cuenta = getCuentaInfo(codigoOId);
    if (!cuenta) return String(codigoOId || 'Sin cuenta');
    return String(`${cuenta.codigo || ''} - ${cuenta.nombre || cuenta.descripcion || ''}`.trim());
  };

  // Filtrar movimientos
  const movimientosFiltrados = useMemo(() => {
    return movimientos.filter(mov => {
      // Filtro por búsqueda
      const busquedaCoincide = !busqueda ||
        mov.folio.toString().includes(busqueda) ||
        mov.glosa?.toLowerCase().includes(busqueda.toLowerCase()) ||
        mov.referencia?.toLowerCase().includes(busqueda.toLowerCase()) ||
        mov.cuenta_banco?.toString().includes(busqueda);

      // Filtro por estado
      let estadoCoincide = true;
      if (filtroEstado) {
        // Si el usuario selecciona un estado específico, usarlo
        estadoCoincide = mov.estado === filtroEstado;
      } else if (mostrarSoloProcesados) {
        // Si no hay filtro específico pero mostrar solo procesados, excluir PENDIENTE
        estadoCoincide = mov.estado !== 'PENDIENTE';
      }
      // Si mostrarSoloProcesados es false y no hay filtroEstado, mostrar todos

      return busquedaCoincide && estadoCoincide;
    });
  }, [movimientos, busqueda, filtroEstado, mostrarSoloProcesados]);

  // Determinar color de chip según estado
  const getColorEstado = (estado) => {
    switch (estado) {
      case 'PENDIENTE':
        return 'warning';
      case 'COMPLETADO':
        return 'info';
      case 'EXPORTADO':
        return 'success';
      default:
        return 'default';
    }
  };

  const calcularMovimientosPendientes = () => {
    return movimientos.filter(m => m.estado === 'PENDIENTE').length;
  };

  // Componente para información secundaria
  const MovimientoSecundario = ({ mov }) => {
    const nombreBanco = getNombreCuenta(mov.cuenta_banco);
    return (
      <span>
        <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={nombreBanco}>
          Banco: {nombreBanco}
        </span>
        <span style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
          <span style={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
            {formatearFecha(mov.fecha)}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
            Folio: {mov.folio}
          </span>
        </span>
      </span>
    );
  };

  if (loading) {
    return (
      <Paper sx={{ height: '600px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ height: '600px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: 'background.default' }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Movimientos del Período
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {movimientos.length} movimientos encontrados
          </Typography>
          {movimientos.length > 0 && (
            <Typography variant="caption" color="warning.main">
              {calcularMovimientosPendientes()} pendientes de completar
            </Typography>
          )}
        </Box>

        {/* Filtros */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar por folio, glosa..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: '200px' }}
          />
          <FormControl size="small" sx={{ minWidth: '140px' }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={filtroEstado}
              label="Estado"
              onChange={(e) => setFiltroEstado(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="PENDIENTE">Pendiente</MenuItem>
              <MenuItem value="COMPLETADO">Completado</MenuItem>
              <MenuItem value="EXPORTADO">Exportado</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={mostrarSoloProcesados ? "Mostrando solo completados y exportados" : "Mostrando todos los movimientos"}>
            <Chip
              label={mostrarSoloProcesados ? "Procesados" : "Todos"}
              onClick={() => setMostrarSoloProcesados(!mostrarSoloProcesados)}
              variant={mostrarSoloProcesados ? "filled" : "outlined"}
              color={mostrarSoloProcesados ? "primary" : "default"}
              size="small"
            />
          </Tooltip>
        </Box>
      </Box>

      <Divider />

      {/* Lista */}
      {movimientosFiltrados.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {movimientos.length === 0
              ? 'No hay movimientos para el período seleccionado'
              : 'No hay movimientos que coincidan con los filtros'}
          </Typography>
        </Box>
      ) : (
        <List disablePadding sx={{ flexGrow: 1, overflow: 'auto' }}>
          {movimientosFiltrados.map((mov) => {
            const esSeleccionado = movimientoSeleccionado && movimientoSeleccionado.id === mov.id;
            const isPendiente = mov.estado === 'PENDIENTE';
            const isExportado = mov.estado === 'EXPORTADO';
            const nombreCuenta = getNombreCuenta(mov.cuenta_id);

            return (
              <React.Fragment key={mov.id}>
                <ListItemButton
                  selected={esSeleccionado}
                  onClick={() => onMovimientoSeleccionado(mov)}
                  sx={{
                    borderLeft: '4px solid',
                    borderLeftColor: isExportado ? 'success.main' : isPendiente ? 'warning.main' : 'info.main',
                    backgroundColor: isExportado ? 'rgba(76, 175, 80, 0.08)' :
                                     !isPendiente ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
                    py: 1,
                    px: 2
                  }}
                >
                  <ListItemText
                    disableTypography
                    sx={{ minWidth: 0, pr: 0 }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 0.5 }}>
                        <Chip
                          label={mov.estado}
                          size="small"
                          variant="outlined"
                          color={getColorEstado(mov.estado)}
                        />
                        <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'nowrap', fontWeight: '600' }}>
                          {mov.ingreso > 0 ? formatearMonto(mov.ingreso) : mov.egreso > 0 ? formatearMonto(mov.egreso) : '-'}
                        </span>
                      </Box>
                    }
                    secondary={<MovimientoSecundario mov={mov} />}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, width: '140px', flexShrink: 0 }}>
                    <Tooltip title={nombreCuenta} arrow>
                      <Box sx={{
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        color: mov.cuenta_id ? 'primary.main' : 'text.secondary',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        width: '100%'
                      }}>
                        {nombreCuenta ? nombreCuenta.split(' - ')[0] : 'Sin código'}
                      </Box>
                    </Tooltip>
                    <Box sx={{
                      fontSize: '0.7rem',
                      color: 'text.secondary',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      width: '100%'
                    }}>
                      {nombreCuenta ? nombreCuenta.split(' - ')[1] : 'Sin cuenta'}
                    </Box>
                  </Box>
                </ListItemButton>
                <Divider />
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default MovimientosTab;
